<template>
  <manual-page
    title="Gestión de Clientes"
    description="Guía completa para consultar y administrar la información de los clientes asignados a tu sucursal."
    :show-video="true"
    :related-links="relatedLinks"
    :action-route="{ name: 'clientsIndex' }"
    action-text="Ir a Clientes"
  >
    <!-- Overview -->
    <h2>Vista General</h2>
    <p>
      La sección de clientes te permite consultar la información de todos los clientes
      asignados a tu sucursal. Puedes ver sus datos de contacto, mascotas registradas,
      planes activos y historial de órdenes.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Desde esta sección puedes:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Ver la lista completa de clientes de tu sucursal</li>
        <li>Buscar clientes por nombre, email o teléfono</li>
        <li>Consultar los datos de contacto de cada cliente</li>
        <li>Ver las mascotas registradas de cada cliente</li>
        <li>Revisar los planes activos y su estado</li>
        <li>Consultar el historial de órdenes</li>
        <li>Ver las direcciones de entrega</li>
      </ul>
    </div>

    <!-- Client List -->
    <h2>Lista de Clientes</h2>
    <p>
      La lista principal muestra todos los clientes asignados a tu sucursal
      con información resumida de cada uno.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Información Mostrada</h3>
          <p class="text-gray-600 mb-2">Cada cliente en la lista muestra:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Nombre:</strong> Nombre completo del cliente</li>
            <li><strong>Email:</strong> Correo electrónico de contacto</li>
            <li><strong>Teléfono:</strong> Número de contacto</li>
            <li><strong>Mascotas:</strong> Cantidad de mascotas registradas</li>
            <li><strong>Estado:</strong> Si tiene plan activo o no</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Ordenar y Filtrar</h3>
          <p class="text-gray-600">
            Puedes ordenar la lista por nombre, fecha de registro o estado.
            También puedes filtrar para ver solo clientes con plan activo
            o sin plan.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Haz clic en cualquier cliente para ver su
        información completa, mascotas y órdenes.
      </p>
    </div>

    <!-- Search -->
    <h2>Buscar Clientes</h2>
    <p>
      La búsqueda te permite encontrar rápidamente a un cliente específico.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Usar el Buscador</h3>
          <p class="text-gray-600">
            Escribe en el campo de búsqueda en la parte superior de la lista.
            Puedes buscar por nombre, email o teléfono del cliente.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Resultados en Tiempo Real</h3>
          <p class="text-gray-600">
            Los resultados se filtran automáticamente mientras escribes.
            La búsqueda funciona con coincidencias parciales, así que no
            necesitas escribir el nombre completo.
          </p>
        </div>
      </div>
    </div>

    <div class="info-box">
      <p class="text-sm text-gray-700">
        <strong>Ejemplo:</strong> Si buscas "María", encontrarás a todos los clientes
        cuyo nombre contenga "María", como "María López" o "Ana María García".
      </p>
    </div>

    <!-- Client Detail -->
    <h2>Detalle del Cliente</h2>
    <p>
      Al seleccionar un cliente, accedes a su perfil completo con toda la información.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Datos de Contacto</h3>
          <p class="text-gray-600 mb-2">Información personal del cliente:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Nombre completo:</strong> Nombre y apellidos</li>
            <li><strong>Email:</strong> Para comunicaciones formales</li>
            <li><strong>Teléfono:</strong> Para contacto directo</li>
            <li><strong>Fecha de registro:</strong> Cuándo se unió</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Direcciones de Entrega</h3>
          <p class="text-gray-600">
            Lista de direcciones registradas por el cliente.
            Cada dirección incluye calle, número, colonia, ciudad y código postal.
            El cliente puede tener varias direcciones y elegir una diferente
            para cada entrega.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Mascotas Registradas</h3>
          <p class="text-gray-600">
            Lista de las mascotas del cliente con acceso directo a cada perfil.
            Puedes ver nombre, especie, raza y si tienen plan activo.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">4</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Planes Activos</h3>
          <p class="text-gray-600">
            Estado de los planes de alimentación: activo, pausado o cancelado.
            Incluye información sobre la frecuencia de entrega y próxima orden.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">5</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Historial de Órdenes</h3>
          <p class="text-gray-600">
            Todas las órdenes del cliente, tanto pasadas como actuales.
            Puedes filtrar por estado o fecha para encontrar órdenes específicas.
          </p>
        </div>
      </div>
    </div>

    <!-- Contact Information -->
    <h2>Contactar al Cliente</h2>
    <p>
      Cuando necesites comunicarte con un cliente, usa la información de contacto
      disponible en su perfil.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Cuándo contactar al cliente:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Coordinar entregas o recolecciones</li>
        <li>Resolver dudas sobre su pedido</li>
        <li>Avisar cambios en fechas de entrega</li>
        <li>Confirmar direcciones de entrega</li>
        <li>Notificar sobre su orden lista para recolección</li>
      </ul>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Cuando contactes a un cliente, sé profesional y amable.
        Identifícate como parte del equipo Trunimal y menciona el motivo de tu llamada.
      </p>
    </div>

    <!-- Orders History -->
    <h2>Historial de Órdenes del Cliente</h2>
    <p>
      El historial te permite revisar todas las órdenes pasadas y actuales
      de un cliente específico.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Ver Historial</h3>
          <p class="text-gray-600">
            Desde el detalle del cliente, accede a la sección de órdenes.
            Verás una lista con todas las órdenes ordenadas por fecha.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Información de Cada Orden</h3>
          <p class="text-gray-600 mb-2">Cada orden muestra:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li>Fecha de entrega</li>
            <li>Mascota destinataria</li>
            <li>Tipo de alimento</li>
            <li>Estado de la orden</li>
            <li>Monto total</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Filtrar Órdenes</h3>
          <p class="text-gray-600">
            Puedes filtrar las órdenes por estado o rango de fechas
            para encontrar información específica más rápidamente.
          </p>
        </div>
      </div>
    </div>

    <div class="info-box">
      <p class="text-sm text-gray-700">
        <strong>Utilidad:</strong> El historial es útil para resolver dudas del cliente
        sobre pedidos anteriores, verificar entregas pasadas o identificar patrones
        en sus preferencias.
      </p>
    </div>

    <!-- Client Notes -->
    <h2>Notas sobre el Cliente</h2>
    <p>
      Algunos clientes tienen notas o instrucciones especiales que debes considerar.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Dónde Ver las Notas</h3>
          <p class="text-gray-600">
            Las notas importantes aparecen destacadas en el perfil del cliente.
            También pueden aparecer en el detalle de las órdenes.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Tipos de Notas Comunes</h3>
          <p class="text-gray-600 mb-2">Las notas pueden incluir:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li>Instrucciones especiales de entrega</li>
            <li>Preferencias de horario</li>
            <li>Observaciones sobre las mascotas</li>
            <li>Alergias o restricciones alimenticias</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Importante:</strong> Siempre revisa las notas antes de preparar
        o entregar un pedido. Las instrucciones especiales pueden afectar
        cómo debes manejar la orden.
      </p>
    </div>

    <!-- Best Practices -->
    <h2>Buenas Prácticas</h2>
    <ul>
      <li><strong>Conoce a tus clientes:</strong> Familiarízate con los clientes frecuentes</li>
      <li><strong>Revisa las notas:</strong> Lee siempre las instrucciones especiales</li>
      <li><strong>Verifica direcciones:</strong> Confirma la dirección antes de enviar</li>
      <li><strong>Sé proactivo:</strong> Si notas algo inusual, contacta al cliente</li>
      <li><strong>Respeta la privacidad:</strong> No compartas información del cliente</li>
      <li><strong>Documenta interacciones:</strong> Si tienes una conversación importante, regístrala</li>
    </ul>

    <!-- Support -->
    <h2>¿Necesitas ayuda?</h2>
    <p>Si tienes problemas con la gestión de clientes:</p>
    <ul>
      <li><strong>WhatsApp:</strong> +52 811 669 1329</li>
      <li><strong>Correo:</strong> soporte@trunimal.com</li>
    </ul>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualClients",
  components: { ManualPage },
  data() {
    return {
      relatedLinks: [
        { route: "manualPets", label: "Mascotas" },
        { route: "manualOrders", label: "Órdenes" },
        { route: "manualProductionSchedule", label: "Programa de Producción" },
        { route: "manualGettingStarted", label: "Primeros Pasos" },
      ],
    };
  },
};
</script>
